<template>
  <page :loading="$apollo.queries.job.loading || !job" :err="err" layoutClass="job-show">
    <b-card class="bg-white border-0 mb-3" no-body v-if="job && job.id">
      <b-row>
        <b-col class="pr-md-0" md="8">
          <b-card class="bg-white job-card h-100" no-body>
            <b-card-header class="bg-white border-0" id="job-header">
              <h3>{{ job.title }}</h3>
            </b-card-header>

            <b-card-body class="py-0">
              <key-stats :job="job"/>
            </b-card-body>

            <!-- <b-card-body class="job-description">
              <img v-if="job.image" class="job-image" :src="job.image" alt="" />
              <markdown v-if="showFullDescription" :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
              <p v-else>{{ job.excerpt }}</p>
              <div class="tags">
                <div class="match-label bg-primary-a1 primary-color">UI</div>
                <div class="match-label bg-primary-a1 primary-color">Design</div>
                <div class="match-label bg-primary-a1 primary-color">Software Engineer</div>
                <div class="match-label bg-primary-a1 primary-color">Accountancy</div>
              </div>
            </b-card-body> -->

            <b-card-body class="job-description" v-if="showFullDescription || !canSyncContacts || job.status !== 'ACTIVE'">
              <img v-if="job.image" class="job-image" :src="job.image" alt="" />
              <markdown :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
            </b-card-body>

            <b-card-footer class="bg-white border-0" v-if="job.status === 'ACTIVE' && canSyncContacts">
              <h4>
                <a href="#" @click.prevent="toggleDescription">
                  <template v-if="showFullDescription">{{ $t('jobs.ctas.hide-description') }}</template>
                  <template v-else>{{ $t('jobs.ctas.show-description') }}</template>
                </a>
              </h4>
            </b-card-footer>
          </b-card>
        </b-col>
        <b-col class="pl-md-0" md="4">
          <b-card class="bg-white buttons-card h-100 px-2 px-md-3 px-xl-5" body-class="px-lg-3 px-xl-5">
            <template v-if="job.status === 'ACTIVE'">
              <share class="d-block d-sm-none my-1 mb-3" :title="$t('jobs.share-modal.title')" :url="shareUrl"
                :providers="canShareJobsWith" :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
                @track="trackShareEvent">
                  <template v-slot:text>
                    <p>{{ $t('jobs.share-modal.description') }}</p>
                  </template>
              </share>
              <b-btn class="d-none d-sm-block my-1 w-100" :disabled="!shareUrl" v-b-modal="`share-${job.id}`" data-tour="share-now" variant="primary">
                <font-awesome-icon icon="share"/> {{ $t('ctas.share-now') }}
              </b-btn>
              <b-modal :ref="`share-${job.id}`" :id="`share-${job.id}`" :hide-header-close="showJobSharedContent" hide-header hide-footer lazy>
                <div v-if="!showJobSharedContent" class="d-flex flex-column align-items-center">
                  <share class="w-100" :title="$t('jobs.share-modal.title')" :url="shareUrl"
                    :providers="canShareJobsWith" :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
                    @modal-close="() => $bvModal.hide(`share-${job.id}`)" @track="trackShareEvent">
                      <template v-slot:text>
                        <p>{{ $t('jobs.share-modal.description') }}</p>
                      </template>
                  </share>
                </div>
                <div v-else class="d-flex flex-column align-items-center">
                  <b-btn variant="primary" class="action-button my-2" :to="{ name: 'JobIndex' }">
                    {{ $t('jobs.ctas.view-all-jobs') }}
                  </b-btn>
                  <span class="d-block my-2">or</span>
                  <b-btn variant="outline-primary" class="action-button my-2 px-3" @click="showJobSharedContent = false">
                    {{ $t('ctas.share-again') }}
                  </b-btn>
                </div>
              </b-modal>

              <b-btn class="my-1 w-100" variant="primary" v-b-modal.job-send-referral>
                <font-awesome-icon icon="envelope"/> Send a referral
              </b-btn>
              <b-modal id="job-send-referral" lazy title="Send a referral" hide-footer>
                <p>Enter your contact's details here &amp; we'll send them an email with details of how to apply. If they do, we'll track it back to you.</p>
                <advanced-form v-bind="sendCandidateReferralForm" @submit="sendCandidateReferral">
                  <template v-slot:formControl-after>
                    <b-form-text class="mt-3"><a href="#" v-b-toggle.learn-more>What happens to this data?</a></b-form-text>
                    <b-collapse id="learn-more">
                      <hr/>
                      <p>When you submit this form, your contact will automatically receive an email with details of how to apply.<br/>
                        <strong>No personal data will be shared</strong> with your recruitment team until your contact applies.</p>
                    </b-collapse>
                  </template>
                </advanced-form>
              </b-modal>

              <template v-if="isViooh">
                <b-btn class="my-1 w-100" variant="primary" v-b-modal.job-upload-cv v-if="true">
                  <font-awesome-icon icon="file-upload"/> Upload a CV
                </b-btn>
                <b-modal id="job-upload-cv" lazy title="Upload a CV" hide-footer>
                  <p>You can apply on behalf of a candidate by visiting the apply page below & submitting an application.</p>
                  <b-btn :href="shareUrl" target="_blank" variant="primary">Submit Application</b-btn>
                </b-modal>
              </template>

              <b-btn class="d-none d-sm-block my-1 w-100" v-if="canEmployeeApply" :disabled="!shareUrl" :href="shareUrl" variant="primary">
                <font-awesome-icon icon="share"/> Apply yourself
              </b-btn>

              <b-btn class="my-1 d-none d-md-block w-100" v-if="canSyncContacts" :to="contactSyncLink" variant="outline-primary">
                <template v-if="contacts && contacts.totalContacts > 0">
                  <font-awesome-icon icon="plug"/> {{ $t('jobs.ctas.sync-more-contacts') }}
                </template>
                <template v-else>
                  <font-awesome-icon icon="plug"/> {{ $t('jobs.ctas.sync-your-contacts') }}
                </template>
              </b-btn>
            </template>
            <b-alert v-else-if="job.status === 'INACTIVE'" show variant="warning">{{ $t('jobs.errors.sorry-job-closed') }}</b-alert>
            <b-alert v-else-if="job.status === 'DELETED'" show variant="danger">{{ $t('jobs.errors.sorry-job-deleted') }}</b-alert>
            <template v-if="isRecruiter">
              <hr/>
              <b-btn v-if="isSuperAdmin || showAddJobsButton" variant="primary" class="my-1 w-100" :to="{ name: 'JobEdit', params: { jobID: job.id } }">
                <font-awesome-icon icon="pencil-alt"/> {{ $t('jobs.ctas.edit-job') }}
              </b-btn>
              <template v-if="company.sitePreferences && company.sitePreferences.isShareLinkEnabled">
                <b-btn variant="primary" class="my-1 w-100" v-b-modal="`quick-share-${job.id}`">
                  <font-awesome-icon icon="share"/> {{ $t('jobs.ctas.get-reshare-link') }}
                </b-btn>
                <b-modal :ref="`quick-share-${job.id}`" :id="`quick-share-${job.id}`" hide-title hide-footer lazy>
                  <div class="d-flex flex-column align-items-center">
                    <p>{{ $t('jobs.share-modal.reshare-description') }}</p>
                    <share :url="quickShareUrl" @track="trackQuickShareEvent"/>
                  </div>
                </b-modal>
              </template>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </b-card>
    <template v-if="job && job.status === 'ACTIVE' && canSyncContacts">
      <contacts-filter id="candidates-results" v-if="!isNativeApp || (contacts && contacts.totalContacts > 0)"
        layout="page" :show-filters-on-load="!isRecruiter" :results="contacts"
        :where="filterContactsWhere" @whereUpdated="filterContactsWhereUpdated"
        @pageUpdated="filterContactsPageUpdated" :order="filterContactsOrder"
        :orderOptions="filterContactsOrderOptions" @orderUpdated="filterContactsOrderUpdated"
        :loading="$apollo.queries.contacts.loading || !contacts">
          <!-- <template v-slot:bar v-if="contacts && contacts.totalContacts > 0">
            <b-card body-class="p-3" v-if="job.standardised === null" class="alert-danger my-3">
              <p class="m-0" v-if="isRecruiter">🔗 <strong>Matches are still processing</strong>, in the meantime you can <b>search candidates</b></p>
              <p class="m-0" v-else>🔗 <strong>Matches are still processing</strong>, in the meantime you can <b>search, share or sync your contacts</b></p>
            </b-card>
            <b-card body-class="p-3" v-else-if="hasNoMatches" class="alert-warning my-3">
              <p class="m-0" v-if="$root.isCommunitySite">💪 No matches found, but you can still share the role or upload a CV</p>
              <p class="m-0" v-else>💪 No matches found, but you can still browse your contacts</p>
            </b-card>
          </template> -->

          <template v-slot:contact-card-actions="{ contact }">
            <template v-if="isRecruiter">
              <b-button class="d-none d-md-block mb-2" variant="primary" @click.prevent="() => basketSelected({ contact })" data-tour="select-candidate">
                <template v-if="selectedContactIDs.includes(contact.id)">{{ $t('contacts.ctas.deselect-candidate') }}</template>
                <template v-else>{{ $t('contacts.ctas.select-candidate') }}</template>
              </b-button>
              <b-button class="d-block d-md-none mb-2" variant="primary" @click.prevent="() => selectOneContact({ contact })" data-tour="refer-candidate">
                {{ $t('contacts.ctas.refer-candidate') }}
              </b-button>
            </template>
            <b-button v-else class="mb-2" variant="primary" @click.prevent="() => selectOneContact({ contact })" data-tour="refer-candidate">
              {{ $t('contacts.ctas.refer-candidate') }}
            </b-button>
            <b-dropdown :text="$t('contacts.hide-cta.prompt')" variant="outline-primary" offset="25px">
              <b-dropdown-item-button @click.prevent="() => contactSetUnsuitable({ contact, type: 'JOB' })">{{ $t('contacts.hide-cta.for-job') }}</b-dropdown-item-button>
              <b-dropdown-item-button @click.prevent="() => contactSetUnsuitable({ contact, type: 'COMPANY' })">{{ $t('contacts.hide-cta.for-company') }}</b-dropdown-item-button>
              <b-dropdown-item-button v-if="!filterContactsWhere.matched && isSuperAdmin" @click.prevent="() => contactCreateMatch({ contact })" style="color: green;">Match with job</b-dropdown-item-button>
            </b-dropdown>
          </template>

          <template v-slot:no-results>
            <b-card class="my-3 my-md-2 py-3 px-5 h-100 filter-row no-results" body-class="p-0 text-center" v-if="contacts && contacts.totalContacts > 0">
              <div class="question-circle bg-primary-a2 mb-4"><span class="primary-color">?</span></div>
              <h3>{{ $t('contacts.errors.no-search-results-title') }}</h3>
              <template v-if="isRecruiter || isNativeApp">
                <p>{{ $t('contacts.errors.no-search-results-description') }}</p>
              </template>
              <template v-else>
                <h4 class="mt-4">{{ $t('contacts.errors.no-search-results-still-share') }}</h4>
                <share centered :url="shareUrl" :providers="canShareJobsWith"
                  :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
                  @track="trackShareEvent"/>
              </template>
            </b-card>
            <b-card class="my-2 py-3 px-5 h-100 filter-row" body-class="p-0 text-center" v-else-if="!isNativeApp">
              <template v-if="job.customFieldsHash && job.customFieldsHash.linkedInSearchURL">
                <h3>Search LinkedIn for relevant contacts</h3>
                <p>Click the LinkedIn button to open a pre-defined search filter for your LinkedIn connections.</p>
                <b-btn class="mr-2 mb-3" :href="job.customFieldsHash.linkedInSearchURL" @click="trackedLiBrowseEvent" target="_blank"
                  variant="linkedin">
                    <font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> Browse your connections
                </b-btn>
                <p>When you find a suitable candidate for this role, share your unique share code in a message to make sure the application is tracked back to you.</p>
                <p class="m-0">Your unique share code:</p>
                <b-btn class="d-block text-truncate px-0" disabled :href="shareUrl" variant="transparent">{{ shareUrl }}</b-btn>
                <b-btn class="text-dark" v-clipboard:copy="shareUrl" variant="light">
                  {{ $t('share.ctas.copy-link') }} <font-awesome-icon icon="link"/>
                </b-btn>

                <hr class="my-3"/>
              </template>

              <h3>{{ $t('jobs.share-prompt.refer-by-sharing') }}</h3>
              <share centered :url="shareUrl" :providers="canShareJobsWith"
                :preview-text="jobShareText" :preview-args="{ jobTitle }"
                @track="value => $emit('trackShareEvent', value)"/>

              <!-- <hr class="my-3"/>

              <h3>{{ $t('jobs.share-prompt.refer-by-syncing') }}:</h3>
              <div class="upload-selector p-0 p-md-3 d-flex justify-content-between align-items-center" data-tour="upload-contacts">
                <b-btn class="mr-1 mr-md-5" :to="{ ...contactSyncLink, query: { provider: 'linkedin' } }"
                  variant="linkedin">{{ $t('onboarding.sync-connections.upload-linkedin.cta') }}</b-btn>
                <b-btn class="mr-1 mr-md-5" :to="{ ...contactSyncLink, query: { provider: 'google' } }"
                  variant="google">{{ $t('onboarding.sync-connections.sync-google.cta') }}</b-btn>
                <b-btn :to="{ ...contactSyncLink, query: { provider: 'microsoft' } }"
                  variant="microsoft">{{ $t('onboarding.sync-connections.sync-microsoft.cta') }}</b-btn>
              </div> -->
            </b-card>
          </template>

          <template v-slot:under-filters v-if="isRecruiter">
            <contacts-basket class="d-none d-md-block my-2"
              :selectedContactIDs="selectedContactIDs" :selectedContactData="selectedContactData"
              @clearContacts="basketClear" @removeContact="basketRemove"
              @submit="() => $refs.basketModal.show()"/>
          </template>
      </contacts-filter>
      <no-ssr v-if="contacts && contacts.totalContacts > 0">
        <hr-ask-for-introductions-modal v-if="isRecruiter" :jobID="jobID" ref="basketModal"
          :selectedContactIDs="selectedContactIDs" @clearBasket="basketClear"/>
        <employee-makes-referral-modal v-else ref="basketModal" :jobID="jobID"
          :selectedContactIDs="selectedContactIDs" @clearBasket="basketClear" />
      </no-ssr>
    </template>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import ms from 'ms';
import Markdown from 'vue-markdown';
import Share, { triggerNativeShare } from '@/components/Share';
import { mapRoot, mapPreferences } from '@src/mixins/root';

import AdvancedForm from '@/components/AdvancedForm';
import Page from '@/components/Page';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import ContactsBasket from '@/components/contacts/basket';
import KeyStats from '@/components/jobs/key-stats';
import ContactsFilter from '@/components/contacts-filter/container';
import JobShowQuery from '@/graphql/queries/JobShow.gql';
import JobShowContactRecruitersQuery from '@/graphql/queries/JobShowContactRecruiters.gql';
import JobShowContactEmployeesQuery from '@/graphql/queries/JobShowContactEmployees.gql';
import ReferralsCreateOneMutation from '@/graphql/mutations/ReferralsCreateOne.gql';

const SET_CONTACT_UNSUITABLE = gql`
mutation setContactAsUnsuitable($input: SetUnsuitableContactInput!) {
  setUnsuitableContact(input: $input) {
    success
  }
}
`;
const SET_CONTACT_MATCH = gql`
mutation setContactAsMatch($input: CreateContactMatchInput!) {
  createContactMatch(input: $input) {
    success
  }
}
`;

export default {
  props: {
    jobID: String,
  },
  components: {
    AdvancedForm,
    Page,
    LoadingSpinner,
    Share,
    Markdown,
    ContactsBasket,
    KeyStats,
    ContactsFilter,
    HrAskForIntroductionsModal: () => import('@/modals/hr-ask-for-introductions'),
    EmployeeMakesReferralModal: () => import('@/modals/employee-makes-referral'),
    ApplicationUploadCvModal: () => import('@/modals/application-upload-cv'),
  },
  data: () => ({
    err: null,
    // firstRun: false,
    filterContactsPage: 1,
    filterContactsLimit: 18,
    filterContactsWhere: {
      // relevance_gte: 30,
      unsuitable: false,
    },
    filterContactsOrder: 'RELEVANCE_DESC',
    filterContactsOrderOptions: {
      'CREATED_DESC': 'Newest first',
      'CREATED_ASC': 'Oldest first',
      // 'UPDATED_DESC': 'Recently updated',
      // 'UPDATED_ASC': 'Least updated',
      'RELEVANCE_DESC': 'Best match',
      'RELEVANCE_ASC': 'Least matched',
    },
    selectedContactIDs: [],
    selectedContactData: {},
    showFullDescription: false,
    showApplyBox: false,
    applyResult: null,
    showJobSharedContent: false
  }),
  created() {
    const { sitePreferences: { canSyncContacts } } = this.company;
    this.showFullDescription = !canSyncContacts;

    if (this.$route.hash === '#description') {
      this.showFullDescription = true;
    }
  },
  apollo: {
    job: {
      query: JobShowQuery,
      variables() {
        const { jobID, filterMatchesRelevance } = this;
        return { jobID, filterMatchesRelevance };
      },
      update: ({ company: { job } = {} }) => job,
      result({ loading, data }) {
        if (process.env.VUE_ENV === 'client' && !loading && data) {
          if (this.job) {
            const { id: jobID } = this.job;
            try {
              if (this.$route.hash === '#candidates') {
                this.showFullDescription = false;
                this.$nextTick(() => this.$scrollTo('#candidates-results'));
              }
              if ([ 'JobShowShare', 'JobShowShareChannelCampaign' ].includes(this.$route.name)) {
                this.$nextTick(() => {
                  const { [`share-${this.job.id}`]: shareRef } = this.$refs;
                  shareRef && shareRef.show();
                });
              }

              // if (this.firstRun) {
              //   try {
              //     // if (this.job.location && this.job.location.id) {
              //     //   const { id } = this.job.location;
              //     //   this.filterContactsWhere.contact = this.filterContactsWhere.contact || {};
              //     //   this.filterContactsWhere.contact.location = { id, latlonWithin: '50mi' };
              //     // }
              //     if (this.hasNoMatches && this.filterContactsWhere.relevance_gte) {
              //       delete this.filterContactsWhere.relevance_gte;
              //     }
              //   } finally {
              //     this.firstRun = false;
              //     this.$apollo.queries.contacts.skip = false;
              //   }
              // }

              // this.$apollo.queries.contacts.skip = false;
            } catch (err) {
              this.$notifyErr(err);
              this.$log.error(err);
            }

            if (this.isLoggedIn) {
              this.$root.captureLog({
                eventName: 'JOB_VISITED',
                job: { id: jobID },
                user: { self: true },
              });
            }
          } else {
            this.$notifyFatalErr(new Error('Job not found'));
          }
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
    },
    contacts: {
      query() {
        return this.isRecruiter ? JobShowContactRecruitersQuery : JobShowContactEmployeesQuery;
      },
      variables() {
        const { jobID, filterContactsPage, filterContactsLimit, filterContactsWhere, filterContactsOrder } = this;
        const { contact } = filterContactsWhere || {};
        return {
          jobID,
          filterContactsPage,
          filterContactsLimit,
          filterContactsWhere,
          filterContactsOrder,
          filterContactsMatchedWhere: contact,
        };
      },
      update(data) {
        if (this.$root.isDemoSite && this.jobID === 'RlYEa') {
          const { filterContactsPage, filterContactsLimit } = this;
          return {
            edges: [],
            page: filterContactsPage,
            limit: filterContactsLimit,
            total: 0,
            totalContacts: 0,
            totalMatches: 0,
            totalPages: 1,
          };
        } else {
          const { job, totalContacts } = (this.isRecruiter ? data.company : data.user) || {};
          const { contactConnections = {}, matchesTotal = {} } = job || {};

          // Set the total
          contactConnections.totalContacts = totalContacts ? totalContacts.total : 0;
          contactConnections.totalMatches = matchesTotal ? matchesTotal.total : 0;

          return contactConnections;
        }
      },
      error(err) {
        this.$notifyErr(err, { prepend: 'Failed to load contacts for job:' });
      },
      skip() {
        return this.canSyncContacts === false;
      },
      debounce: ms('1s'),
      deep: true,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    selectContacts: {
      query() {
        return this.isRecruiter ? JobShowContactRecruitersQuery : JobShowContactEmployeesQuery;
      },
      variables() {
        const { jobID } = this;
        const selectContactIDs = (this.$route.query || {}).contactIDs ? `${this.$route.query.contactIDs}`.split(',') : null;
        return {
          jobID,
          filterContactsPage: 1,
          filterContactsLimit: selectContactIDs.length,
          filterContactsWhere: { contact: { id_in: selectContactIDs } },
        };
      },
      async result({ loading, data }) {
        if (!loading && data && (data.company || data.user)) {
          const { job: { contactConnections = {} } = {} } = (this.isRecruiter ? data.company : data.user) || {};
          if (contactConnections && Array.isArray(contactConnections.edges) && contactConnections.edges.length) {
            contactConnections.edges.forEach(({ entry }) => this.basketSelected({ contact: entry }));
          }
        }
      },
      skip() {
        const selectContactIDs = (this.$route.query || {}).contactIDs ? `${this.$route.query.contactIDs}`.split(',') : null;
        return this.canSyncContacts === false || !Array.isArray(selectContactIDs) || selectContactIDs.length === 0;
      },
      debounce: ms('1s'),
      deep: true,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      manual: true,
    },
  },
  metaInfo() {
    const head = {};

    if (this.job) {
      const { title } = this.job;
      head.title = title;
    }

    return head;
  },
  computed: {
    ...mapRoot([
      'company', 'user', 'isLoggedIn', 'isRecruiter', 'isSuperAdmin', 'isNativeApp',
      'isCommunitySite', 'isSainsburys', 'isPayPal', 'isViooh', 'isAssurant',
      'canSyncContacts', 'isHelloFresh'
    ]),
    ...mapPreferences([ 'canEmployeeApply' ]),
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l’annonce et postulez maintenant !';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
        }

        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
    }),
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    hasNoMatches() {
      return this.job && this.job.matchesTotal && this.job.matchesTotal.total === 0;
    },
    contactSyncLink() {
      return {
        name: 'Onboarding',
        params: {
          nextRoute: { name: 'JobShow', params: { jobID: this.jobID } },
          stepName: 'registernetwork',
        },
      };
    },
    shareUrl() {
      const { origin } = this.$root;
      const { shareCode } = this.job || {};
      return shareCode ? `${origin}/r/${shareCode}` : null;
    },
    quickShareUrl() {
      const { origin } = this.$root;
      const { id: jobID } = this.job || {};
      return jobID ? `${origin}/rs/${jobID}` : null;
    },
    showCvUploadBox() {
      const { showCvUploadBox = true } = this.company.sitePreferences;
      return showCvUploadBox;
    },
    showAddJobsButton() {
      const { showAddJobsButton = false } = this.company.sitePreferences;
      return showAddJobsButton;
    },
    sendCandidateReferralForm() {
      const form = {
        fullName: {
          label: 'Contact Name',
          placeholder: 'Example Name',
          required: true,
          type: 'STRING',
        },
        emailAddress: {
          label: 'E-mail Address',
          placeholder: 'email@example.com',
          required: true,
          type: 'STRING',
          options: {
            autocomplete: 'off',
          },
          validation: {
            isEmailAddress: true,
          },
        },
      };

      return {
        autocomplete: 'off',
        layout: 'inline-md-3',
        submitLabel: 'Send',
        form,
      };
    },
  },
  methods: {
    toggleDescription() {
      this.showFullDescription = !this.showFullDescription;
      this.$scrollTo('#job-header');
    },
    filterContactsPageUpdated(newPage) {
      this.filterContactsPage = newPage;
      this.$scrollTo('.contacts-filter-container');
    },
    filterContactsWhereUpdated(newWhere) {
      this.filterContactsWhere = newWhere;
      this.filterContactsPage = 1;
    },
    filterContactsOrderUpdated(newOrder) {
      this.filterContactsOrder = newOrder;
      this.filterContactsPage = 1;
    },
    basketSelected({ contact }) {
      const { id } = contact;
      if (this.selectedContactIDs.includes(id)) {
        this.selectedContactIDs.splice(this.selectedContactIDs.indexOf(id), 1);
        delete this.selectedContactData[id];
      } else {
        this.selectedContactIDs.push(id);
        this.selectedContactData[id] = contact;
      }
    },
    basketRemove(id) {
      if (this.selectedContactIDs.includes(id)) {
        this.selectedContactIDs.splice(this.selectedContactIDs.indexOf(id), 1);
        delete this.selectedContactData[id];
      }
    },
    basketClear() {
      this.selectedContactIDs = [];
      this.selectedContactData = {};
    },
    selectOneContact({ contact }) {
      const { id } = contact;
      if (!this.selectedContactIDs.includes(id)) {
        this.selectedContactIDs.push(id);
        this.selectedContactData[id] = contact;
      }
      this.$refs.basketModal.show();
    },
    async contactSetUnsuitable({ contact: { id }, type }) {
      try {
        const input = {
          contactID: id,
          // reason: prompt('Why?', null),
        };

        if (type === 'JOB') {
          Object.assign(input, {
            jobID: this.jobID,
          });
        }

        await this.$apollo.mutate({
          mutation: SET_CONTACT_UNSUITABLE,
          variables: { input },
        });

        this.basketRemove(id);
        this.$apollo.queries.contacts.refetch();
      } catch (err) {
        if (err.networkError && err.networkError.result && Array.isArray(err.networkError.result.errors)) {
          this.err = err.networkError.result.errors.reduce((s, { message }) => s.concat(message), []).join('\n');
        } else {
          this.err = `${err}`;
        }
      }
    },
    async contactCreateMatch({ contact: { id } }) {
      try {
        const input = {
          contactID: id,
          jobID: this.jobID,
          relevance: Math.max(0, Math.min(prompt('Match %', 25), 100)),
        };

        await this.$apollo.mutate({
          mutation: SET_CONTACT_MATCH,
          variables: { input },
        });

        this.basketRemove(id);
        this.$apollo.queries.contacts.refetch();
      } catch (err) {
        if (err.networkError && err.networkError.result && Array.isArray(err.networkError.result.errors)) {
          this.err = err.networkError.result.errors.reduce((s, { message }) => s.concat(message), []).join('\n');
        } else {
          this.err = `${err}`;
        }
      }
    },
    triggerNativeShare() {
      triggerNativeShare(this.shareUrl, () => this.trackShareEvent({ channel: 'native' }));
    },
    trackShareEvent({ channel }) {
      const { shareCode } = this.job;
      Promise.resolve(this.$root.captureLog({
        eventName: 'JOB_SHARED',
        job: { share: shareCode },
        user: { share: shareCode },
        referral: { share: shareCode },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      }));
      if ([ 'JobShowShare', 'JobShowShareChannelCampaign' ].includes(this.$route.name)) {
        this.showJobSharedContent = true;
      }
    },
    trackQuickShareEvent({ channel }) {
      Promise.resolve(this.$root.captureLog({
        eventName: 'JOB_QUICK_LINK_SHARED',
        job: { id: this.job.id },
        user: { id: this.user.id },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      }));
    },
    async sendCandidateReferral(form) {
      this.$log.debug({ form });
      try {
        const { fullName, emailAddress } = form;
        this.$assert(fullName, 'Missing contact name');
        this.$assert(emailAddress, 'Missing contact email');

        const create = {
          job: { id: this.jobID },
          user: { self: true },
          fullName,
          emailAddress,
          status: 'SUGGESTED',
        };

        this.$log.debug({ create });

        await this.$apollo.mutate({
          mutation: ReferralsCreateOneMutation,
          variables: { create },
        });

        this.$notification({
          title: 'Referral Sent',
          message: 'An email has been sent to your contact',
          variant: 'success',
        });
        this.$bvModal.hide('job-send-referral');
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
    async trackedLiBrowseEvent() {
      await this.$root.captureLog({
        eventName: 'JOB_BROWSE_LINKEDIN',
        job: { id: this.jobID },
        user: { self: true },
      });
    },
  },
};
</script>
<style lang="scss">
.page .job-show {

  .loading-panel {
    margin: 20px 0 20px 0;
    text-align: center;
  }

  .horizontal-filter {
    margin-top: 1.4em;
  }
  .alert-danger {
    color: #721c24;
    background-color: #f8d7da;
    border-color: #f5c6cb;
  }

  .card {
    &.job-card {
      @media (min-width: 768px) {
        border-right: 0;
      }
      @media (max-width: 768px) {
        border-bottom: 0;
      }
    }

    .card-footer {
      text-align: center;
      h4 {
        vertical-align: middle;
      }
    }

    .job-edit-button {
      position: absolute;
      right: 15px;
    }

    .job-image {
      display: block;
      max-width: 150px;
      max-height: 150px;
      width: auto;
      height: auto;
    }

    .job-description {
      flex-direction: column;
      h1 {
        font-size: 1.3em;
      }
      h2 {
        font-size: 1.2em;
      }
    }

    .tags {
      display: inline-flex;
      .match-label {
        padding-left: 0.5em;
        padding-right: 0.5em;
        width: inherit;
        font-weight: 400;
        text-transform: inherit;
        margin-right: 0.7em;
        height: inherit;
      }
    }
  }

  .contacts-list {
    padding-top: 1em;
    display: flex;
    flex-direction: row;
    flex-flow: row wrap;
    margin-left: -1em;
    margin-right: -1em;
  }

  .filter-row {
    &.no-results {
      text-align: center;
    }
    .question-circle {
      margin: 0 auto;
      width: 80px;
      height: 80px;
      border-radius: 100%;
      color: white;
      vertical-align: middle;
      font-size: 50px;
      font-weight: bolder;
      span {
        text-align: center;
      }
    }
  }

  .contact-col {
    flex-basis: calc(100%/3);
    padding: 1em;
  }
  .upload-selector {
    max-width: 650px;
    margin: 0 auto;
    .btn:hover {
      opacity: 0.8;
    }
  }

  .sticky-top {
    top: 10px;
    z-index: 900;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/jobs/jobShow.vue